// TaskStatusPanel.js - 우측 하단 작업 상태 패널
import React, { useState } from 'react';
import { 
  FiCheck, FiAlertCircle, FiRefreshCw, FiChevronDown, FiChevronUp,
  FiClock, FiTrash2, FiXCircle
} from 'react-icons/fi';
import { useTasks } from '../contexts/TaskContext';
import { useTheme } from '../contexts/ThemeContext';

function TaskStatusPanel() {
  const { runningTasks, tasks, cancelTask, deleteTask, clearCompletedTasks } = useTasks();
  const { isDark } = useTheme();
  const [isExpanded, setIsExpanded] = useState(true);
  const [showAll, setShowAll] = useState(false);

  // 표시할 작업 목록 결정
  const displayTasks = showAll 
    ? tasks.filter(t => ['running', 'completed', 'failed'].includes(t.status)).slice(0, 10)
    : runningTasks;

  // 최근 완료된 작업이 있으면 표시 (5초 이내)
  const recentCompletedTasks = tasks.filter(t => 
    t.status === 'completed' && 
    t.completedAt && 
    (Date.now() - new Date(t.completedAt).getTime()) < 5000
  );

  // 실행 중인 작업이 있거나 최근 완료된 작업이 있으면 표시
  const hasTasksToShow = displayTasks.length > 0 || recentCompletedTasks.length > 0;
  
  // 표시할 것이 없고 showAll도 false이면 패널 숨김
  if (!hasTasksToShow && !showAll) {
    return null;
  }
  
  // 최근 완료된 작업도 표시 목록에 추가
  const finalDisplayTasks = showAll 
    ? displayTasks
    : [...displayTasks, ...recentCompletedTasks.slice(0, 3)];

  const getStatusIcon = (status) => {
    switch (status) {
      case 'running':
        return <FiRefreshCw className="spinning" size={16} color="#00BFFF" />;
      case 'completed':
        return <FiCheck size={16} color="#66BB6A" />;
      case 'failed':
        return <FiAlertCircle size={16} color="#FF5252" />;
      case 'pending':
        return <FiClock size={16} color="#FFA726" />;
      case 'cancelled':
        return <FiXCircle size={16} color="#999" />;
      default:
        return null;
    }
  };

  const getStatusText = (status) => {
    switch (status) {
      case 'running':
        return '실행 중';
      case 'completed':
        return '완료';
      case 'failed':
        return '실패';
      case 'pending':
        return '대기 중';
      case 'cancelled':
        return '취소됨';
      default:
        return status;
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'running':
        return '#00BFFF';
      case 'completed':
        return '#66BB6A';
      case 'failed':
        return '#FF5252';
      case 'pending':
        return '#FFA726';
      case 'cancelled':
        return '#999';
      default:
        return '#999';
    }
  };

  const formatTime = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now - date;
    const diffSec = Math.floor(diffMs / 1000);
    const diffMin = Math.floor(diffSec / 60);
    const diffHour = Math.floor(diffMin / 60);

    if (diffSec < 60) return `${diffSec}초 전`;
    if (diffMin < 60) return `${diffMin}분 전`;
    if (diffHour < 24) return `${diffHour}시간 전`;
    return date.toLocaleString('ko-KR');
  };

  const handleCancel = async (taskId) => {
    if (window.confirm('이 작업을 취소하시겠습니까?')) {
      await cancelTask(taskId);
    }
  };

  const handleDelete = async (taskId) => {
    if (window.confirm('이 작업을 삭제하시겠습니까?')) {
      await deleteTask(taskId);
    }
  };

  return (
    <>
      {/* 작업 패널 */}
      <div style={{
        position: 'fixed',
        bottom: '20px',
        right: '80px',
        width: isExpanded ? '400px' : '200px',
        maxHeight: isExpanded ? '500px' : '60px',
        backgroundColor: isDark ? '#1E1E1E' : '#FFFFFF',
        border: `1px solid ${isDark ? '#333' : '#E0E0E0'}`,
        borderRadius: '12px',
        boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
        zIndex: 1000,
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
        transition: 'all 0.3s ease'
      }}>
        {/* 헤더 */}
        <div
          onClick={() => setIsExpanded(!isExpanded)}
          style={{
            padding: '16px',
            borderBottom: isExpanded ? `1px solid ${isDark ? '#333' : '#E0E0E0'}` : 'none',
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            backgroundColor: isDark ? '#252525' : '#F5F5F5'
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
            <FiRefreshCw size={18} color="#00BFFF" className={runningTasks.length > 0 ? 'spinning' : ''} />
            <div>
              <div style={{ fontSize: '14px', fontWeight: '600', color: isDark ? '#FAFAFA' : '#1a1a1a' }}>
                작업 진행 상황
              </div>
              <div style={{ fontSize: '12px', color: isDark ? '#999' : '#666', marginTop: '2px' }}>
                {finalDisplayTasks.filter(t => t.status === 'running').length > 0
                  ? `${finalDisplayTasks.filter(t => t.status === 'running').length}개 실행 중`
                  : finalDisplayTasks.length > 0
                  ? `${finalDisplayTasks.length}개 작업`
                  : '실행 중인 작업 없음'}
              </div>
            </div>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            {displayTasks.length > 0 && (
              <span style={{
                fontSize: '12px',
                color: '#00BFFF',
                fontWeight: '600',
                padding: '4px 8px',
                backgroundColor: isDark ? '#1a2a3a' : '#e6f7ff',
                borderRadius: '12px'
              }}>
                {displayTasks.length}
              </span>
            )}
            {isExpanded ? <FiChevronDown size={18} /> : <FiChevronUp size={18} />}
          </div>
        </div>

        {/* 작업 목록 */}
        {isExpanded && (
          <>
            <div style={{
              flex: 1,
              overflowY: 'auto',
              padding: '12px'
            }}>
              {displayTasks.length === 0 ? (
                <div style={{
                  padding: '20px',
                  textAlign: 'center',
                  color: isDark ? '#999' : '#666',
                  fontSize: '13px'
                }}>
                  진행 중인 작업이 없습니다
                </div>
              ) : (
                finalDisplayTasks.map(task => (
                  <div
                    key={task.id}
                    style={{
                      padding: '12px',
                      backgroundColor: isDark ? '#2A2A2A' : '#F9F9F9',
                      borderRadius: '8px',
                      marginBottom: '8px',
                      border: `1px solid ${isDark ? '#333' : '#E0E0E0'}`
                    }}
                  >
                    {/* 작업 헤더 */}
                    <div style={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      marginBottom: '8px'
                    }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flex: 1 }}>
                        {getStatusIcon(task.status)}
                        <div style={{ flex: 1, minWidth: 0 }}>
                          <div style={{
                            fontSize: '13px',
                            fontWeight: '600',
                            color: isDark ? '#FAFAFA' : '#1a1a1a',
                            whiteSpace: 'nowrap',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis'
                          }}>
                            {task.name}
                          </div>
                          <div style={{
                            fontSize: '11px',
                            color: isDark ? '#999' : '#666',
                            marginTop: '2px'
                          }}>
                            {formatTime(task.created_at)}
                          </div>
                        </div>
                      </div>
                      
                      {/* 액션 버튼 */}
                      <div style={{ display: 'flex', gap: '4px' }}>
                        {task.status === 'running' && (
                          <button
                            onClick={() => handleCancel(task.id)}
                            style={{
                              background: 'transparent',
                              border: 'none',
                              cursor: 'pointer',
                              padding: '4px',
                              display: 'flex',
                              alignItems: 'center',
                              color: '#FF5252',
                              borderRadius: '4px'
                            }}
                            title="취소"
                            onMouseEnter={(e) => e.currentTarget.style.backgroundColor = isDark ? '#3a2a2a' : '#ffe6e6'}
                            onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
                          >
                            <FiXCircle size={16} />
                          </button>
                        )}
                        {['completed', 'failed', 'cancelled'].includes(task.status) && (
                          <button
                            onClick={() => handleDelete(task.id)}
                            style={{
                              background: 'transparent',
                              border: 'none',
                              cursor: 'pointer',
                              padding: '4px',
                              display: 'flex',
                              alignItems: 'center',
                              color: isDark ? '#999' : '#666',
                              borderRadius: '4px'
                            }}
                            title="삭제"
                            onMouseEnter={(e) => e.currentTarget.style.backgroundColor = isDark ? '#333' : '#f0f0f0'}
                            onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
                          >
                            <FiTrash2 size={16} />
                          </button>
                        )}
                      </div>
                    </div>

                    {/* 상태 및 메시지 */}
                    <div style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '8px',
                      marginBottom: '8px'
                    }}>
                      <span style={{
                        fontSize: '11px',
                        padding: '2px 6px',
                        borderRadius: '4px',
                        backgroundColor: getStatusColor(task.status) + '20',
                        color: getStatusColor(task.status),
                        fontWeight: '600'
                      }}>
                        {getStatusText(task.status)}
                      </span>
                      {task.message && (
                        <span style={{
                          fontSize: '11px',
                          color: isDark ? '#999' : '#666'
                        }}>
                          {task.message}
                        </span>
                      )}
                    </div>

                    {/* 진행률 바 */}
                    {task.status === 'running' && (
                      <div style={{
                        width: '100%',
                        height: '6px',
                        backgroundColor: isDark ? '#1a1a1a' : '#e0e0e0',
                        borderRadius: '3px',
                        overflow: 'hidden'
                      }}>
                        <div style={{
                          width: `${task.progress}%`,
                          height: '100%',
                          backgroundColor: '#00BFFF',
                          transition: 'width 0.3s ease',
                          borderRadius: '3px'
                        }} />
                      </div>
                    )}

                    {/* 에러 메시지 */}
                    {task.status === 'failed' && task.error && (
                      <div style={{
                        fontSize: '11px',
                        color: '#FF5252',
                        marginTop: '8px',
                        padding: '8px',
                        backgroundColor: isDark ? '#2a1a1a' : '#ffebee',
                        borderRadius: '4px'
                      }}>
                        {task.error}
                      </div>
                    )}
                  </div>
                ))
              )}
            </div>

            {/* 푸터 */}
            <div style={{
              padding: '12px',
              borderTop: `1px solid ${isDark ? '#333' : '#E0E0E0'}`,
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center'
            }}>
              <button
                onClick={() => setShowAll(!showAll)}
                style={{
                  background: 'transparent',
                  border: 'none',
                  cursor: 'pointer',
                  fontSize: '12px',
                  color: '#00BFFF',
                  fontWeight: '600',
                  padding: '4px 8px',
                  borderRadius: '4px'
                }}
                onMouseEnter={(e) => e.currentTarget.style.backgroundColor = isDark ? '#1a2a3a' : '#e6f7ff'}
                onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
              >
                {showAll ? '실행 중만 보기' : '전체 보기'}
              </button>
              
              {showAll && tasks.filter(t => ['completed', 'failed'].includes(t.status)).length > 0 && (
                <button
                  onClick={async () => {
                    if (window.confirm('완료된 작업을 모두 삭제하시겠습니까?')) {
                      await clearCompletedTasks();
                    }
                  }}
                  style={{
                    background: 'transparent',
                    border: `1px solid ${isDark ? '#333' : '#E0E0E0'}`,
                    cursor: 'pointer',
                    fontSize: '12px',
                    color: isDark ? '#999' : '#666',
                    fontWeight: '600',
                    padding: '4px 8px',
                    borderRadius: '4px'
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.borderColor = '#FF5252';
                    e.currentTarget.style.color = '#FF5252';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.borderColor = isDark ? '#333' : '#E0E0E0';
                    e.currentTarget.style.color = isDark ? '#999' : '#666';
                  }}
                >
                  모두 삭제
                </button>
              )}
            </div>
          </>
        )}
      </div>

      {/* CSS for spinning animation */}
      <style>
        {`
          .spinning {
            animation: spin 1s linear infinite;
          }
          
          @keyframes spin {
            from {
              transform: rotate(0deg);
            }
            to {
              transform: rotate(360deg);
            }
          }
        `}
      </style>
    </>
  );
}

export default TaskStatusPanel;

